var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/x-filter-editor.ts
import { getTypeString } from "@adornis/baseql/baseqlTypes.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { ComponentSizeController } from "@adornis/chemistry/controllers/component-size-controller.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-button.js";
import "@adornis/chemistry/elements/components/x-chip.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-hr.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import { XDialog } from "@adornis/dialog/x-dialog.js";
import "@adornis/dragdrop/x-drag-drop.js";
import { FieldConfiguration, makeUniqueIDForFieldConf } from "@adornis/field-management/field-configuration.js";
import { translateFieldName } from "@adornis/field-management/translate-field.js";
import "@adornis/field-management/x-field-selection-drawer.js";
import "@adornis/forms/x-plain-input.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { Subject, debounceTime, filter, map, switchMap, takeUntil, tap } from "rxjs";
import { AbstractAspect } from "../aspect-schemas/AbstractAspect.js";
import { changeStream } from "../change-stream.js";
import {
  buildFullOptions,
  constructFilterAspect,
  getBaseQLFieldForField
} from "../filter-options.js";
function chooseIcon(field) {
  if (!field)
    return "settings";
  switch (getTypeString(field.type)) {
    case "String":
      return "description";
    case "DateTime":
      return "date_range";
    case "Int":
      return "123";
    case "Float":
      return "percent";
    case "Boolean":
      return "check_box";
    default:
      return "settings";
  }
}
var XFilterEditor = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.noCollapse = new RXController(this, false);
    this.options = new RXController(this, void 0);
    this.isFilterVisible = new RXController(this, false);
    this.filter = new RXController(
      this,
      this.options.observable.pipe(
        filter(Boolean),
        switchMap((opts) => opts.filter.whenChanging)
      )
    );
    this._translation = new TranslationController(this);
    this._componentSize = new ComponentSizeController(this, { debounceTime: 1e3 });
    this._searchFieldChanger = new Subject();
    this._fieldSelectionDrawer = createRef();
  }
  get fullOptions() {
    if (!this._fullOptions)
      throw new Error("no full options available yet");
    return this._fullOptions;
  }
  get isSmall() {
    return (this._componentSize.value?.width ?? 0) < 600;
  }
  willUpdate(chp) {
    super.willUpdate(chp);
    if (chp.has("options") && this.options.value)
      this._fullOptions = buildFullOptions(this.options.value);
    if (!this.fullOptions.filter.aspects.length)
      this.isFilterVisible.next(false);
  }
  swapAspect(instance, index) {
    if (!this.options.value)
      throw new Error("cant happen");
    this.options.value.filter.aspects.splice(index, 1, instance);
    this._dispatchFilterChange();
    this.requestUpdate();
  }
  _dispatchFilterChange() {
    const event = new CustomEvent("filter-changed", { detail: { value: this.options.value?.filter } });
    this.dispatchEvent(event);
  }
  connectedCallback() {
    super.connectedCallback();
    this._searchFieldChanger.pipe(debounceTime(220), takeUntil(this.disconnected)).subscribe((t) => {
      if (!this.options.value?.filter || !this.options.value.filter.search && !t)
        return;
      this.options.value.filter.search = t;
    });
    this.options.observable.pipe(
      filter(Boolean),
      switchMap(
        (options) => changeStream(options.filter.whenChanging.pipe(map((filter2) => filter2.toObject()))).pipe(
          filter(Boolean),
          filter((ch) => !!ch.length),
          filter((ch) => ch.some((ch2) => !!ch2.getPath().length)),
          // filter out setting a completely new view, that's only done on initialization
          filter(
            (ch) => ch.some(
              (ch2) => !ch2.concernsPath(["search", "searchFields", "sortingFields", "sortingDirections", "aspects"])
            )
          ),
          tap(
            (x) => console.log(
              "bubbling changes to persistence",
              x.map((c) => `${c.getPath().join(".")} - ${c.getValue()}`)
            )
          ),
          map(() => options)
        )
      ),
      debounceTime(100)
      // avoid deadlocks. not a real fix but does quite well. Fix if it acts up
    ).subscribe((options) => this.fullOptions.onFilterChanged(options.filter));
  }
  _isFilterVisible() {
    return this.noCollapse.value || this.isFilterVisible.value;
  }
  render() {
    if (!this.options.value?.filter)
      return "no options or filter";
    return xComponents(
      html`
        ${this._isFilterVisible() ? this.bigFilter() : this.smallFilterBar()}
        <x-field-selection-drawer ${ref(this._fieldSelectionDrawer)}></x-field-selection-drawer>
      `
    );
  }
  getActionButtons() {
    return html`
      <x-button
        icon="add"
        mode=${this._isFilterVisible() || !this.options.value?.filter.aspects.length ? "text" : "icon"}
        @click=${async () => this.addFilter()}
      >
        ${this._translation.translate("filter", "addFilter")}
      </x-button>
      ${this.options.value?.filter.aspects.length ? html`
            <x-button
              mode=${this._isFilterVisible() ? "text" : "icon"}
              icon="delete"
              @click=${async () => {
      this.isFilterVisible.next(false);
      if (this.options.value?.filter.aspects)
        this.options.value.filter.aspects = [];
      this.dispatchEvent(new CustomEvent("filter-reset"));
    }}
            >
              ${this._translation.translate("filter", "resetFilter")}
              ${this._isFilterVisible() ? nothing : html`
                    <x-tooltip> <x-text> ${this._translation.translate("filter", "resetFilter")} </x-text> </x-tooltip>
                  `}
            </x-button>
          ` : nothing}
      ${this._isFilterVisible() ? html`
            <x-button
              mode="text"
              icon="check"
              @click=${() => {
      this.isFilterVisible.next(false);
      this.dispatchEvent(new CustomEvent("filter-apply", { detail: this.options.value?.filter }));
    }}
            >
              ${this._translation.translate("filter", "applyFilter")}
            </x-button>
          ` : nothing}
    `;
  }
  smallFilterBar() {
    return html`
      <x-flex ?horizontal=${!this._isFilterVisible()} wrap crossaxis-center space="sm">
        ${!this.noCollapse.value && this.options.value?.filter.aspects.length ? html`
              <x-button
                mode="icon"
                icon=${this._isFilterVisible() ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                @click=${() => this.isFilterVisible.next(!this.isFilterVisible.value)}
              >
              </x-button>
            ` : nothing}
        ${this.searchBox()}
        ${this.isSmall ? !this.options.value?.filter.aspects.length ? nothing : html` <x-text> ${this.options.value?.filter.aspects.length ?? "0"} Filter </x-text> ` : repeat(
      this.options.value?.filter.aspects ?? [],
      (aspect) => makeUniqueIDForFieldConf(aspect),
      (aspect, index) => this._renderAspectChip(aspect, index)
    )}

        <x-flex ?horizontal=${!this._isFilterVisible()} ?crossaxis-center=${!this._isFilterVisible()} space="sm">
          ${this.getActionButtons()}
          <slot name="bigFilterEnd"></slot>
        </x-flex>
      </x-flex>
    `;
  }
  searchBox() {
    return this.options.value?.searchable ? html`
          <x-input
            flex
            placeholder=${this._translation.translate("filter", "search")}
            placeholder-mode="inline"
            icon="search"
            ${css({ minWidth: "120px" })}
            .value=${this.options.value.filter?.search}
            @value-changed=${(e) => {
      e.detail.value !== void 0 && this._searchFieldChanger.next(e.detail.value ?? "");
      this._dispatchFilterChange();
    }}
            clearable
          >
          </x-input>
        ` : nothing;
  }
  bigFilter() {
    const hideButton = !this.noCollapse.value && this.options.value?.filter.aspects.length ? html`
            <x-button mode="text" icon="keyboard_arrow_up" @click=${() => this.isFilterVisible.next(false)}>
              ${this._translation.translate("filter", "hideFilter")}
            </x-button>
          ` : nothing;
    return html`
      <x-flex space="sm">
        <x-flex horizontal crosaxis-center space-between>
          <x-flex space="sm" horizontal crossaxis-center>
            ${hideButton} ${this.searchBox()} ${this.getActionButtons()}
          </x-flex>
          <slot name="bigFilterEnd"></slot>
        </x-flex>
        ${this.options.value?.filter.aspects.length ? html`
              <x-hr></x-hr>
              ${this.operatorGrid()}
            ` : nothing}
      </x-flex>
    `;
  }
  async addFilter() {
    if (!this.fullOptions)
      throw new Error("missing options");
    const fieldSelectionDrawer = this._fieldSelectionDrawer.value;
    if (!fieldSelectionDrawer)
      throw new Error("Could not find field selection drawer");
    const result = await fieldSelectionDrawer.chooseFields({
      title: this._translation.translate("filter", "addFilter"),
      ...this.fullOptions.fieldAspectGeneration,
      selected: this.options.value?.filter.aspects.map((aspect) => new FieldConfiguration(aspect)),
      extraFields: Object.values(this.fullOptions.fullIdentifierOverrides ?? {}).concat(Object.values(this.fullOptions.fullFieldNameOverrides ?? {})).flatMap(
        (configs) => (Array.isArray(configs) ? configs : [configs]).map(
          (config) => new FieldConfiguration({ customID: config.onlyForIdentifier, fieldName: config.onlyForField })
        )
      )
      // TODO actively omitted atm due to Stakeholder wishes. if necessary, make configurable
      // onSelectedChanged: selected =>
      //   (this.fullOptions.filter.aspects = selected.map(fieldConf => this.fullOptions.possiblyCreateAspect(fieldConf))),
    });
    this.fullOptions.filter.aspects = result.map((fieldConf) => this.fullOptions.possiblyCreateAspect(fieldConf));
    this.options.value?.onFilterChanged?.(this.fullOptions.filter);
    this.isFilterVisible.next(true);
  }
  operatorGrid() {
    const options = this.options.value;
    if (!options)
      return nothing;
    const isSmall = this.isSmall;
    return xComponents(html`
      <x-drag-drop
        .degrees-on-move=${0}
        drag-mode="column"
        child-selector=${".draggable"}
        @resorted=${async (e) => {
      if (!options.filter.aspects)
        return;
      options.filter.aspects = options.filter.aspects.sort((a, b) => {
        return e.detail.order.indexOf(a.fieldName ?? a.customID ?? "") - e.detail.order.indexOf(b.fieldName ?? b.customID ?? "");
      });
      this.requestUpdate();
    }}
      >
        <x-grid
          columns=${!isSmall ? "max-content max-content max-content minmax(max-content, 400px) max-content" : "1fr"}
          space="sm"
          ${css({ alignItems: "center", alignSelf: "start" })}
        >
          ${repeat(
      options.filter.aspects ?? [],
      (aspect) => AbstractAspect.resolveID(aspect),
      (aspect, i) => {
        const availableConstructors = this.fullOptions.getAspectConfigAlternatives(aspect).map((config) => config.aspectConstructor);
        const filterNameToken = aspect.fieldName ?? aspect.customID ?? "";
        const editor = this.fullOptions.getAspectConfig(aspect)?.renderEditor({
          aspectValue: aspect,
          translation: this._translation
        });
        const deleteButton = html`
                <x-button
                  mode="icon"
                  icon="delete"
                  @click=${() => options.filter.aspects.splice(options.filter.aspects.indexOf(aspect), 1)}
                  ${css({ marginTop: "-5px", marginBottom: "-5px" })}
                ></x-button>
              `;
        return xComponents(html`
                <x-grid
                  space=${isSmall ? "xxs" : "sm"}
                  class="draggable"
                  key=${aspect.fieldName ?? aspect.customID ?? ""}
                  ${css({
          gridTemplateColumns: "subgrid",
          gridColumn: "1 / -1",
          alignItems: "center",
          alignSelf: "start"
        })}
                >
                  ${!isSmall ? html`
                        <x-icon class="handle" ${css({ color: this.colors.neutral, fontSize: "1.6rem" })}>
                          drag_indicator
                        </x-icon>
                      ` : nothing}
                  <x-flex horizontal space-between crossaxis-center>
                    <x-text> ${translateFieldName(filterNameToken)} </x-text>
                    ${isSmall ? deleteButton : nothing}
                  </x-flex>
                  ${availableConstructors.length > 1 ? html`
                        <x-dropdown-selection
                          placeholder="Operator"
                          placeholder-mode="hide"
                          ${css({ width: isSmall ? "100%" : "220px" })}
                          .selectables=${availableConstructors}
                          .value=${aspect.constructor}
                          .renderString=${(aspectConstructor) => this._translation.translate("class", aspectConstructor._class)}
                          @value-picked=${(e) => {
          if (!e.detail.value)
            return;
          this.swapAspect(constructFilterAspect(e.detail.value, aspect), i);
        }}
                        >
                        </x-dropdown-selection>
                      ` : html` <x-text tone="subtle"> ${this._translation.translate("class", aspect._class)} </x-text> `}
                  ${editor === nothing ? html`<div></div>` : editor ?? html`
                        <x-text @click=${() => XDialog.alert(JSON.stringify(aspect))}>
                          ${this._translation.translate("filter", "noEditor")}
                        </x-text>
                      `}
                  ${isSmall ? nothing : deleteButton}
                </x-grid>
              `);
      }
    )}
        </x-grid>
      </x-drag-drop>
    `);
  }
  _renderAspectChip(aspectValue, index) {
    const field = this.fullOptions.fieldAspectGeneration?.class ? getBaseQLFieldForField(this.fullOptions.fieldAspectGeneration.class, aspectValue) : null;
    const renderAspectConfig = this.fullOptions.getAspectConfig(aspectValue);
    if (!renderAspectConfig)
      return this._invalidAspectChip(index);
    const preview = renderAspectConfig.renderPreview({ aspectValue, translation: this._translation });
    const complete = preview !== null;
    const filterNameToken = aspectValue.customID ?? aspectValue.fieldName;
    return xComponents(html`
      <x-chip
        ${css({
      background: (complete ? this.colors.tone.success : this.colors.neutral).shade(100),
      color: this.colors.on.tone.success,
      border: `2px solid ${complete ? this.colors.tone.success : this.colors.neutral}`,
      cursor: "pointer"
    })}
        @click=${() => this.isFilterVisible.next(!this.isFilterVisible.value)}
      >
        <x-flex space="xs" horizontal crossaxis-center>
          <x-icon> ${chooseIcon(field)}</x-icon>
          <x-flex space="xxs" crossaxis-center horizontal>
            <x-text> ${filterNameToken ? translateFieldName(filterNameToken) : "No customID or fieldName"} </x-text>
          </x-flex>
          <x-text>
            ${this._translation.translate("class", aspectValue._class)}${preview && preview !== nothing || !complete ? ":" : ""}
          </x-text>
          <x-text> ${complete ? preview : this._translation.translate("filter", "incomplete")} </x-text>
        </x-flex>
      </x-chip>
    `);
  }
  _invalidAspectChip(index) {
    return html`
      <x-chip
        ${css({
      background: "#fff",
      color: this.colors.accent,
      border: `2px solid ${this.colors.tone.error}`,
      cursor: "pointer"
    })}
        @click=${() => {
      this.options.value?.filter.aspects.splice(index, 1);
      this._dispatchFilterChange();
      this.requestUpdate();
    }}
      >
        <x-flex space="xs" horizontal crossaxis-center>
          <x-icon ${css({ fontSize: "1.6em" })}> error </x-icon>
          <x-text> ${this._translation.translate("filter", "invalidAspect")} </x-text>
        </x-flex>
      </x-chip>
    `;
  }
};
__decorateClass([
  property({ attribute: "no-collapse", type: Boolean })
], XFilterEditor.prototype, "noCollapse", 2);
__decorateClass([
  property({ attribute: false })
], XFilterEditor.prototype, "options", 2);
__decorateClass([
  property({ attribute: "is-filter-visible", type: Boolean })
], XFilterEditor.prototype, "isFilterVisible", 2);
__decorateClass([
  state()
], XFilterEditor.prototype, "filter", 2);
__decorateClass([
  state()
], XFilterEditor.prototype, "_translation", 2);
__decorateClass([
  state()
], XFilterEditor.prototype, "_componentSize", 2);
XFilterEditor = __decorateClass([
  customElement("x-filter-editor")
], XFilterEditor);
export {
  XFilterEditor,
  chooseIcon
};
